import { ApolloClient, gql, isApolloError, NormalizedCacheObject, QueryOptions } from '@apollo/client'
import { getCookie } from 'cookies-next'
import { ServerResponse } from 'http'
import { AUTH_JWT_COOKIE } from '@/constants/cookies'
import type { User } from '@/services/UserService/UserService'
import { GET_USER_PROFILE_QUERY } from '@/services/UserService/queries'
import { GuildPlan, UserProfileQuery } from '@/types/codegen-federation'
import { decodeAccessToken } from '../auth-utils'
import { logger } from '../logging'
import { getStringOrEmptyString, getValidString } from '../primitives/strings'
import { Maybe, ServerRequest } from '../types'

export function getAccessTokenFromServer(req: ServerRequest, res?: ServerResponse): string {
  return getStringOrEmptyString(getValidString(getCookie(AUTH_JWT_COOKIE, { req, res })))
}

export function getUserUuidFromJwt(req: ServerRequest, res?: ServerResponse): string | undefined {
  const accessToken = getAccessTokenFromServer(req, res)
  return decodeAccessToken(accessToken)?.userId
}

export function getUserIdAndAccessToken(
  req: ServerRequest,
  res?: ServerResponse,
): { userId?: string; accessToken?: string } {
  const accessToken = getValidString(getCookie(AUTH_JWT_COOKIE, { req, res }))
  if (!accessToken) return {}
  const decoded = decodeAccessToken(accessToken)
  if (!decoded?.userId) return {}
  return { userId: decoded.userId, accessToken }
}

export async function getUser(
  client: ApolloClient<object>,
  userId: Maybe<string>,
  options?: QueryOptions,
): Promise<User | null> {
  if (!userId) {
    return null
  }

  try {
    const result = await client.query<UserProfileQuery>({
      query: GET_USER_PROFILE_QUERY,
      fetchPolicy: 'no-cache',
      errorPolicy: 'none',
      ...options,
    })
    if (result.errors?.length) {
      logger().error('Failed to fetch user profile', { errors: result.errors })
      return null
    }
    return result.data?.user ?? null
  } catch (e) {
    const err = e as Error
    if (isApolloError(err)) {
      if (err.graphQLErrors.length && err.graphQLErrors.some((e) => e.message === 'Not Authorized')) {
        logger().warn(`User not authorized. Auth token is either missing or invalid`, { err })
        return null
      }
    }
    logger().error('Failed to fetch user profile', { err })
    return null
  }
}

export interface GuildStatus {
  isGuildMember: boolean
  guildMemberStart: Date | null
  guildMemberEnd: Date | null
  guildMemberReason: string
  guildPlan: GuildPlan | null
  paymentPlatform?: string | null
  guildRoles: {
    hasEarlyAccess: boolean
    hasFreeTickets: boolean
    hasMerchDiscount: boolean
  }
  guildPermissions: {
    hasFreeTickets: boolean
    hasMerchDiscount: boolean
    hasEarlyAccess: boolean
    canVote: boolean
    adFree: boolean
    publicViewingRight: boolean
    limitedAds: boolean
  }
}

export const GET_USER_PUBLIC_PROFILE = gql`
  query GetUser($uuid: ID!, $fallbackToInitials: Boolean) {
    publicUserProfile(uuid: $uuid) {
      firstName
      lastName
      image(fallbackToInitials: $fallbackToInitials)
      id
      city
      state
      country
    }
  }
`

export const getUserPublicProfile = async (
  client: ApolloClient<NormalizedCacheObject>,
  fallbackToInitials = true,
  uuid?: string,
) => {
  const { data } = await client.query({
    query: GET_USER_PUBLIC_PROFILE,
    variables: { uuid, fallbackToInitials },
    errorPolicy: 'all',
  })
  return data.publicUserProfile
}

export interface UserPublicProfile {
  firstName: string
  lastName: string
  image: string
  id: string
  city?: string
  state?: string
  country?: string
}

export const getEmptyGuildStatus = (): GuildStatus => {
  return {
    isGuildMember: false,
    guildPlan: 'BRONZE',
    guildMemberStart: null,
    guildMemberEnd: null,
    guildMemberReason: '',
    paymentPlatform: null,
    guildRoles: {
      hasEarlyAccess: false,
      hasFreeTickets: false,
      hasMerchDiscount: false,
    },
    guildPermissions: {
      hasFreeTickets: false,
      hasMerchDiscount: false,
      hasEarlyAccess: false,
      canVote: false,
      adFree: false,
      publicViewingRight: false,
      limitedAds: false,
    },
  }
}
